import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { useReactToPrint } from "react-to-print";
import { Trash2, Pencil, Check, X, Edit2, Printer } from "lucide-react";
import api from "@/lib/api";
import { TextField } from "@/components/FormField";
import { PrintHeader, PrintFooter } from "@/components/PrintHeader";
import DocumentosSection from "@/components/DocumentosSection";
import { formatText, formatDays, formatCurrencyInput, parseCurrency, fechaMX } from "@/lib/format";

export default function ClienteModal({ cliente, mode, setMode, onClose, onSave, isAdmin }) {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [activando, setActivando] = useState(false);
  const [priceLists, setPriceLists] = useState([]);
  const [users, setUsers] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [editDescTipo, setEditDescTipo] = useState(null);
  const [editDescPct, setEditDescPct] = useState("");
  const printRef = useRef(null);
  const isView = mode === "view";

  useEffect(() => {
    if (cliente) {
      setFormData({
        nombreRazonSocial: cliente.nombre_razon_social || "", rfc: cliente.rfc || "", nombreContacto: cliente.nombre_contacto || "",
        telefono: cliente.telefono || "", whatsapp: cliente.whatsapp || "", email: cliente.email || "",
        calle: cliente.calle || "", noExterior: cliente.no_exterior || "", noInterior: cliente.no_interior || "",
        colonia: cliente.colonia || "", municipioAlcaldia: cliente.municipio_alcaldia || "", estado: cliente.estado || "", codigoPostal: cliente.codigo_postal || "",
        entregaUsarDatosCliente: cliente.entrega_usar_datos_cliente || false, entregaRazonSocial: cliente.entrega_razon_social || "",
        entregaNombreRecibe: cliente.entrega_nombre_recibe || "", entregaCalleNumero: cliente.entrega_calle_numero || "",
        entregaColonia: cliente.entrega_colonia || "", entregaMunicipio: cliente.entrega_municipio || "", entregaEstado: cliente.entrega_estado || "",
        entregaCodigoPostal: cliente.entrega_codigo_postal || "", entregaTelefono: cliente.entrega_telefono || "", entregaUbicacionMaps: cliente.entrega_ubicacion_maps || "",
        listaPrecios: cliente.lista_precios || "", condicionesPago: cliente.condiciones_pago || "", tiempoEntrega: cliente.tiempo_entrega || "",
        vigenciaCotizacion: cliente.vigencia_cotizacion || "",
        limiteCredito: cliente.limite_credito ? `$${new Intl.NumberFormat("en-US").format(cliente.limite_credito)}` : "",
        diasCredito: cliente.dias_credito ?? "",
        fechaAutorizacion: cliente.fecha_autorizacion || "", vendedor: cliente.vendedor_asignado_id || "", autorizo: cliente.autorizo || "",
        descuentoTipo: "", descuentoPorcentaje: "",
        descuentos: Array.isArray(cliente.descuentos) ? cliente.descuentos.filter((d) => !d.isBase) : [],
      });
    }
  }, [cliente]);

  useEffect(() => {
    const cargar = () => Promise.all([api.get("/price-lists"), api.get("/users"), api.get("/descuentos")]).then(([pl, us, ds]) => {
      setPriceLists(pl.data || []); setUsers(us.data || []); setCatalogo((ds.data || []).filter((d) => d.activo));
    });
    cargar();
    window.addEventListener("focus", cargar);
    return () => window.removeEventListener("focus", cargar);
  }, []);

  const set = (id, value) => setFormData((p) => ({ ...p, [id]: value }));
  const handleChange = (e) => { if (isView) return; set(e.target.id, formatText(e.target.id, e.target.value)); };
  const handleDaysChange = (e) => { if (isView) return; set(e.target.id, formatDays(e.target.id, e.target.value, formData[e.target.id] || "")); };
  const handleCurrency = (e) => { if (isView) return; set(e.target.id, formatCurrencyInput(e.target.value)); };

  const handleCheckbox = (checked) => {
    if (isView) return;
    setFormData((prev) => {
      const n = { ...prev, entregaUsarDatosCliente: checked };
      if (checked) {
        n.entregaRazonSocial = prev.nombreRazonSocial; n.entregaNombreRecibe = prev.nombreContacto;
        n.entregaCalleNumero = `${prev.calle} ${prev.noExterior} ${prev.noInterior || ""}`.replace(/\s+/g, " ").trim();
        n.entregaColonia = prev.colonia; n.entregaMunicipio = prev.municipioAlcaldia; n.entregaEstado = prev.estado;
        n.entregaCodigoPostal = prev.codigoPostal; n.entregaTelefono = prev.telefono;
      } else {
        ["entregaRazonSocial", "entregaNombreRecibe", "entregaCalleNumero", "entregaColonia", "entregaMunicipio", "entregaEstado", "entregaCodigoPostal", "entregaTelefono"].forEach((k) => (n[k] = ""));
      }
      return n;
    });
  };

  const addDescuento = () => {
    if (!formData.descuentoTipo) return toast.error("Seleccione un descuento");
    if (formData.descuentos.some((d) => d.tipo === formData.descuentoTipo)) return toast.error("Este descuento ya fue agregado");
    const cat = catalogo.find((c) => c.id === formData.descuentoTipo);
    if (!cat) return;
    setFormData((prev) => ({
      ...prev,
      descuentos: [...prev.descuentos, { tipo: cat.id, tipoLabel: cat.nombre, porcentaje: cat.porcentaje }],
      descuentoTipo: "",
    }));
  };
  const removeDescuento = (tipo) => setFormData((prev) => ({ ...prev, descuentos: prev.descuentos.filter((d) => d.tipo !== tipo) }));
  const startEditDescuento = (d) => { setEditDescTipo(d.tipo); setEditDescPct(String(d.porcentaje ?? "")); };
  const cancelEditDescuento = () => { setEditDescTipo(null); setEditDescPct(""); };
  const saveEditDescuento = (tipo) => {
    setFormData((prev) => ({ ...prev, descuentos: prev.descuentos.map((d) => (d.tipo === tipo ? { ...d, porcentaje: Number(editDescPct) || 0 } : d)) }));
    setEditDescTipo(null); setEditDescPct("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) return;
    if (!formData.nombreRazonSocial || !formData.rfc || !formData.email || !formData.calle) return toast.error("Por favor completa todos los campos obligatorios.");
    setLoading(true);
    try {
      const payload = {
        nombre_razon_social: formData.nombreRazonSocial, rfc: formData.rfc, nombre_contacto: formData.nombreContacto,
        telefono: formData.telefono, whatsapp: formData.whatsapp, email: formData.email,
        calle: formData.calle, no_exterior: formData.noExterior, no_interior: formData.noInterior,
        colonia: formData.colonia, municipio_alcaldia: formData.municipioAlcaldia, estado: formData.estado, codigo_postal: formData.codigoPostal,
        entrega_usar_datos_cliente: formData.entregaUsarDatosCliente, entrega_razon_social: formData.entregaRazonSocial,
        entrega_nombre_recibe: formData.entregaNombreRecibe, entrega_calle_numero: formData.entregaCalleNumero,
        entrega_colonia: formData.entregaColonia, entrega_municipio: formData.entregaMunicipio, entrega_estado: formData.entregaEstado,
        entrega_codigo_postal: formData.entregaCodigoPostal, entrega_telefono: formData.entregaTelefono, entrega_ubicacion_maps: formData.entregaUbicacionMaps,
        lista_precios: formData.listaPrecios, condiciones_pago: formData.condicionesPago, tiempo_entrega: formData.tiempoEntrega,
        vigencia_cotizacion: formData.vigenciaCotizacion, limite_credito: parseCurrency(formData.limiteCredito),
        dias_credito: formData.diasCredito ? Number(formData.diasCredito) : null,
        fecha_autorizacion: formData.fechaAutorizacion || null, vendedor_asignado_id: formData.vendedor || null, autorizo: formData.autorizo,
        descuentos: formData.descuentos,
      };
      const { data } = await api.put(`/clientes/${cliente.id}`, payload);
      toast.success("Cliente actualizado exitosamente");
      onSave(data);
    } catch (err) {
      toast.error(`Error al actualizar: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: `Cliente_${cliente?.id_cliente || "Ficha"}` });

  const handleActivarCredito = async () => {
    setActivando(true);
    try {
      const { data } = await api.post(`/clientes/${cliente.id}/activar-credito`);
      toast.success("Crédito activado");
      onSave(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo activar el crédito");
    } finally {
      setActivando(false);
    }
  };

  const fld = (id, label, opts = {}) => (
    <TextField id={id} label={label} value={formData[id]} onChange={opts.onChange || handleChange} type={opts.type} readOnly={isView || opts.readOnly} testid={`ficha-${id}`} />
  );

  return (
    <Dialog open={!!cliente} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90dvh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-2 border-b">
          <div className="flex justify-between items-center pr-4">
            <div>
              <DialogTitle className="text-2xl">{isView ? "Ficha del Cliente" : "Editar Cliente"}</DialogTitle>
              <DialogDescription>{cliente?.id_cliente} - {cliente?.nombre_razon_social}</DialogDescription>
            </div>
            {isView && isAdmin && (
              <div className="flex gap-2">
                <Button onClick={handlePrint} variant="outline" size="sm" data-testid="ficha-print-btn"><Printer className="h-4 w-4 mr-2" /> Imprimir a PDF</Button>
                <Button onClick={() => setMode("edit")} variant="outline" size="sm" data-testid="ficha-edit-btn"><Edit2 className="h-4 w-4 mr-2" /> Editar</Button>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6" ref={printRef}>
          <PrintHeader title="Ficha del Cliente" folio={cliente?.id_cliente} fecha={fechaMX(new Date())} />
          <form id="cliente-form" onSubmit={handleSubmit} className="space-y-8">
            {cliente?.credito_estatus === "pendiente_firma" && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="credito-pendiente-banner">
                <div className="text-sm text-amber-900">
                  <p className="font-semibold">Crédito pendiente de firma</p>
                  <p>Sube el <b>Contrato firmado</b> y el <b>Pagaré firmado</b> en la sección Documentos y luego activa el crédito.</p>
                </div>
                {isAdmin && <Button type="button" onClick={handleActivarCredito} disabled={activando} data-testid="activar-credito-btn">{activando ? "Activando…" : "Activar crédito"}</Button>}
              </div>
            )}
            {cliente?.credito_estatus === "activo" && (
              <div className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-800 font-medium" data-testid="credito-activo-banner">Crédito activo ✓</div>
            )}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold">1. Datos del Cliente</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fld("nombreRazonSocial", "Nombre del Cliente / Razón Social *")}
                {fld("rfc", "RFC *")}
                {fld("nombreContacto", "Nombre del Contacto *")}
                {fld("email", "Email *", { type: "email" })}
                {fld("telefono", "Teléfono *")}
                {fld("whatsapp", "WhatsApp")}
              </div>
            </section>
            <section className="space-y-4">
              <h3 className="text-lg font-semibold">2. Dirección Fiscal</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">{fld("calle", "Calle *")}</div>
                {fld("noExterior", "No. Exterior *")}
                {fld("noInterior", "No. Interior")}
                {fld("colonia", "Colonia *")}
                {fld("municipioAlcaldia", "Municipio / Alcaldía *")}
                {fld("estado", "Estado *")}
                {fld("codigoPostal", "Código Postal *")}
              </div>
            </section>
            <section className="space-y-4">
              <h3 className="text-lg font-semibold">3. Información de Entrega</h3>
              <div className="flex items-center space-x-2 bg-muted/50 p-4 rounded-md">
                <Checkbox id="entregaUsarDatosCliente" checked={formData.entregaUsarDatosCliente || false} onCheckedChange={handleCheckbox} disabled={isView} />
                <Label htmlFor="entregaUsarDatosCliente">Usar datos del cliente</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fld("entregaRazonSocial", "Razón Social", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaNombreRecibe", "Nombre de quien recibe", { readOnly: formData.entregaUsarDatosCliente })}
                <div className="md:col-span-2">{fld("entregaCalleNumero", "Calle y Número", { readOnly: formData.entregaUsarDatosCliente })}</div>
                {fld("entregaColonia", "Colonia", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaMunicipio", "Municipio", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaEstado", "Estado", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaCodigoPostal", "Código Postal", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaTelefono", "Teléfono", { readOnly: formData.entregaUsarDatosCliente })}
                {fld("entregaUbicacionMaps", "Ubicación Google Maps (URL)")}
              </div>
            </section>
            <section className="space-y-4">
              <h3 className="text-lg font-semibold">4. Condiciones Comerciales</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lista de Precios</Label>
                  <Select value={formData.listaPrecios || ""} onValueChange={(v) => set("listaPrecios", v)} disabled={isView}>
                    <SelectTrigger><SelectValue placeholder="Seleccione lista" /></SelectTrigger>
                    <SelectContent>{priceLists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Condiciones de Pago</Label>
                  <Select value={formData.condicionesPago || ""} onValueChange={(v) => set("condicionesPago", v)} disabled={isView}>
                    <SelectTrigger><SelectValue placeholder="Seleccione condición" /></SelectTrigger>
                    <SelectContent><SelectItem value="Contado">Contado</SelectItem><SelectItem value="Crédito">Crédito</SelectItem></SelectContent>
                  </Select>
                </div>
                {fld("tiempoEntrega", "Tiempo de Entrega", { onChange: handleDaysChange })}
                {fld("vigenciaCotizacion", "Vigencia de Cotización", { onChange: handleDaysChange })}
                {fld("limiteCredito", "Límite de Crédito", { onChange: handleCurrency })}
                <div className="space-y-2">
                  <Label htmlFor="diasCredito">Días de crédito</Label>
                  <Input id="diasCredito" type="number" min="0" value={formData.diasCredito || ""} onChange={(e) => set("diasCredito", e.target.value)} readOnly={isView} />
                </div>
                {fld("fechaAutorizacion", "Fecha de autorización", { type: "date", onChange: (e) => set("fechaAutorizacion", e.target.value) })}
                <div className="space-y-2">
                  <Label>Vendedor</Label>
                  <Select value={formData.vendedor || ""} onValueChange={(v) => set("vendedor", v)} disabled={isView || !isAdmin}>
                    <SelectTrigger><SelectValue placeholder="Seleccione vendedor" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>ID</Label>
                  <Input value={users.find((u) => u.id === formData.vendedor)?.numero_identificacion || ""} readOnly disabled className="bg-muted" />
                </div>
                <div className="space-y-2">
                  <Label>Quién lo autorizó</Label>
                  <Select value={formData.autorizo || ""} onValueChange={(v) => set("autorizo", v)} disabled={isView}>
                    <SelectTrigger><SelectValue placeholder="Seleccione usuario" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </section>
            <section className="space-y-4">
              <h3 className="text-lg font-semibold">5. Descuentos</h3>
              {!isView && (
                <div className="flex flex-col md:flex-row items-end gap-4 mb-4">
                  <div className="space-y-2 flex-1">
                    <Label>Descuento</Label>
                    <Select value={formData.descuentoTipo || ""} onValueChange={(v) => set("descuentoTipo", v)}>
                      <SelectTrigger data-testid="descuento-select"><SelectValue placeholder="Seleccione un descuento" /></SelectTrigger>
                      <SelectContent>
                        {catalogo.length === 0
                          ? <div className="px-3 py-2 text-sm text-muted-foreground">No hay descuentos cargados. Créelos en Configuración → Descuentos.</div>
                          : catalogo.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre} — {c.porcentaje}%</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="button" onClick={addDescuento} variant="secondary" data-testid="add-descuento-btn">Agregar</Button>
                </div>
              )}
              {formData.descuentos && formData.descuentos.length > 0 ? (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader><TableRow><TableHead>Tipo de Descuento</TableHead><TableHead>Porcentaje</TableHead>{!isView && <TableHead className="w-[120px] text-right">Acciones</TableHead>}</TableRow></TableHeader>
                    <TableBody>
                      {formData.descuentos.map((d) => (
                        <TableRow key={d.tipo}>
                          <TableCell className="font-medium">{d.tipoLabel}</TableCell>
                          <TableCell>
                            {!isView && editDescTipo === d.tipo
                              ? <Input type="number" min="0" step="0.01" value={editDescPct} onChange={(e) => setEditDescPct(e.target.value)} className="h-8 w-24" data-testid={`edit-desc-input-${d.tipo}`} autoFocus />
                              : `${d.porcentaje}%`}
                          </TableCell>
                          {!isView && (
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {editDescTipo === d.tipo ? (
                                  <>
                                    <Button variant="ghost" size="icon" type="button" title="Guardar" onClick={() => saveEditDescuento(d.tipo)} className="text-emerald-600" data-testid={`save-desc-${d.tipo}`}><Check className="h-4 w-4" /></Button>
                                    <Button variant="ghost" size="icon" type="button" title="Cancelar" onClick={cancelEditDescuento} data-testid={`cancel-desc-${d.tipo}`}><X className="h-4 w-4" /></Button>
                                  </>
                                ) : (
                                  <>
                                    <Button variant="ghost" size="icon" type="button" title="Editar" onClick={() => startEditDescuento(d)} data-testid={`edit-desc-${d.tipo}`}><Pencil className="h-4 w-4" /></Button>
                                    <Button variant="ghost" size="icon" type="button" title="Eliminar" onClick={() => removeDescuento(d.tipo)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : <p className="text-sm text-muted-foreground">No hay descuentos asignados.</p>}
            </section>
            <section className="space-y-4">
              <h3 className="text-lg font-semibold">6. Documentos</h3>
              <DocumentosSection entidadTipo="cliente" entidadId={cliente?.id} canManage={isAdmin} tipos={["INE", "Comprobante de domicilio", "Carta de referencias", "Contrato firmado", "Pagaré firmado", "Acta constitutiva", "Poder del representante legal", "Constancia de situación fiscal (RFC)"]} />
            </section>
          </form>
          <PrintFooter />
        </div>

        {!isView && (
          <DialogFooter className="p-6 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
            <Button type="submit" form="cliente-form" disabled={loading} data-testid="ficha-save-btn">{loading ? "Guardando..." : "Guardar Cambios"}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
